import { handleCustomUDL } from "@utils/UDLUtils";
import { dispatchScrollTo } from "@atoms/_page-controller/PageController";

export class CourseDetailV2 {
  private el: HTMLElement;
  private abortController: AbortController;

  constructor(el: HTMLElement) {
    this.el = el;
    this.abortController = new AbortController();
  }

  init(): void {
    const signal = this.abortController.signal;

    this.bindMoreDatesLink(signal);
    this.bindBookNowClick(signal);
    this.bindGetDirectionClick(signal);
    this.bindContactMailClick(signal);
    this.bindContactPhoneClick(signal);
    this.bindDownloadClick(signal);
    this.bindPreviousCoursesClick(signal);
  }

  private bindMoreDatesLink(signal: AbortSignal): void {
    const link = this.el.querySelector<HTMLElement>(".course-detail__more-dates-link");
    if (!link) return;

    link.addEventListener("click", (e) => {
      e.preventDefault();
      const target = document.getElementById("course-detail__more-dates--anchor");
      if (target) {
        dispatchScrollTo(target);
      }
    }, { signal });
  }

  private bindBookNowClick(signal: AbortSignal): void {
    const handler = (e: Event) => {
      const eventName = "course_detail_book_now";
      const cardParams: Record<string, unknown> = this.getCommonParameters();
      cardParams.link_url = (e.currentTarget as HTMLAnchorElement).href || "";
      cardParams.qualified_traffic_from_web = true;
      this.pushData(cardParams, eventName);
    };

    // Header CTA button (inside component)
    this.el.querySelectorAll<HTMLElement>(".course-detail__header--cta .button").forEach((btn) => {
      btn.addEventListener("click", handler, { signal });
    });

    // Sticky nav CTA button (outside component scope)
    document.querySelectorAll<HTMLElement>(".nav-bar-wrapper.course-detail-cta .comp-button > .button").forEach((btn) => {
      btn.addEventListener("click", handler, { signal });
    });
  }

  private bindGetDirectionClick(signal: AbortSignal): void {
    this.el.querySelectorAll<HTMLElement>(".map__locations a").forEach((link) => {
      link.addEventListener("click", () => {
        this.pushData(this.getCommonParameters(), "course_detail_get_directions_click");
      }, { signal });
    });
  }

  private bindContactMailClick(signal: AbortSignal): void {
    this.el.querySelectorAll<HTMLElement>('.course-detail__contact a[href^="mailto:"]').forEach((link) => {
      link.addEventListener("click", () => {
        this.pushData(this.getCommonParameters(), "course_detail_contact_mail");
      }, { signal });
    });
  }

  private bindContactPhoneClick(signal: AbortSignal): void {
    this.el.querySelectorAll<HTMLElement>('.course-detail__contact a[href^="tel:"]').forEach((link) => {
      link.addEventListener("click", () => {
        this.pushData(this.getCommonParameters(), "course_detail_phone_number");
      }, { signal });
    });
  }

  private bindDownloadClick(signal: AbortSignal): void {
    this.el.querySelectorAll<HTMLElement>(".comp-downloadlist .entry-link").forEach((link) => {
      link.addEventListener("click", (e) => {
        const target = e.currentTarget as HTMLAnchorElement;
        const cardParams: Record<string, unknown> = this.getCommonParameters();
        const titleEl = target.querySelector<HTMLElement>(".title");
        const fileNameText = titleEl ? titleEl.innerText.split(".") : [""];
        cardParams.link_url = target.href || "";
        cardParams.link_text = fileNameText.join(".") || "";
        cardParams.file_type = fileNameText.pop() || "";
        cardParams.file_name = fileNameText.join(".") || "";
        this.pushData(cardParams, "course_detail_download_click");
      }, { signal });
    });
  }

  private bindPreviousCoursesClick(signal: AbortSignal): void {
    const parent = this.el.parentElement;
    if (!parent) return;

    const parentParent = parent.parentElement;
    if (!parentParent) return;

    parentParent.querySelectorAll<HTMLElement>(":scope > .section .button").forEach((btn) => {
      if (parent.contains(btn)) return;
      btn.addEventListener("click", (e) => {
        const target = e.currentTarget as HTMLAnchorElement;
        const cardParams: Record<string, unknown> = this.getCommonParameters();
        cardParams.link_url = target.href || "";
        cardParams.link_text = target.textContent || "";
        this.pushData(cardParams, "course_detail_previous_courses_click");
      }, { signal });
    });
  }

  private getCommonParameters(): Record<string, string> {
    const h2 = this.el.querySelector<HTMLElement>(".course-detail__header h2");
    return {
      course_id: this.el.dataset.courseId || "",
      course_name: h2 ? h2.textContent || "" : "",
    };
  }

  private pushData(cardParams: Record<string, unknown>, eventName: string): void {
    handleCustomUDL(eventName, cardParams);
  }

  destroy(): void {
    this.abortController.abort();
  }

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="course-detail-v2"]')
      .forEach((el) => {
        new CourseDetailV2(el).init();
      });
  }
}
